import { MarketLocation, useAnnualOverview, useLocale } from '@e2m-customer-portal/hooks';
import {
  convertMeasure,
  getMonthNumber,
  isBattery,
  isBiogas,
  isControlEnergy,
  isFluctuating,
} from '@e2m-customer-portal/shared';
import { Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import { setMonth, startOfToday } from 'date-fns';
import { range } from 'ramda';
import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AnnualOverviewRow } from './annual-overview-row/AnnualOverviewRow';
import { ErrorComponent } from '@e2m-customer-portal/ui';

export interface FeedInAnnualOverviewProps {
  marketLocation: MarketLocation;
  year: number;
  isMobile?: boolean;
}

export function FeedInAnnualOverview({
  marketLocation,
  year,
  isMobile,
}: Readonly<FeedInAnnualOverviewProps>): ReactElement<FeedInAnnualOverviewProps> {
  const { t } = useTranslation();
  const theme = useTheme();
  const { code: locale } = useLocale();
  const [data, error] = useAnnualOverview(marketLocation.id, year);

  const styles = {
    shared: 'py-2 bg-blue-grey-200 text-right text-white',
  };

  if (error) return <ErrorComponent error={error} />;

  return (
    <div className="grid grid-cols-[2fr_repeat(13,1fr)] overflow-x-auto">
      <div
        className={clsx(
          styles.shared,
          'sticky left-0 flex flex-col items-center justify-end border-0 border-r border-solid border-grey-200 pl-6 pr-4 shadow-lg shadow-blue-grey-200 md:shadow-none xl:flex-row',
        )}
        data-testid="grid-header-cell"
      />

      {range(0, 12).map((month, index) => {
        const date = setMonth(new Date(), month);
        const isCurrentMonth = date.getMonth() === new Date().getMonth();

        return (
          <div
            key={month}
            className={clsx(
              styles.shared,
              'flex min-w-fit items-center justify-center border-0 border-l border-solid border-grey-200',
              {
                'border-l-0': !index,
              },
            )}
            data-testid="grid-header-cell"
          >
            <span
              className={clsx(isCurrentMonth ? 'px-2 my-auto pb-1 leading-none rounded-full bg-green-200' : 'mb-1')}
            >
              <Typography className="min-[1800px]:hidden" variant="body3">
                {date.toLocaleDateString(locale, { month: 'short' })}
              </Typography>
              <Typography className="hidden min-[1800px]:inline-block" variant="body3">
                {date.toLocaleDateString(locale, { month: 'long' })}
              </Typography>
            </span>
          </div>
        );
      })}

      <div
        className={clsx(
          styles.shared,
          'flex items-center justify-end border-0 border-l border-solid border-grey-200 pl-4 pr-6',
        )}
        data-testid="grid-header-cell"
      >
        <Typography variant="body3">{t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_YEAR')}</Typography>
      </div>

      {!isFluctuating(marketLocation.technologies) && !marketLocation.linkedMarketLocation && (
        <AnnualOverviewRow
          latestFeedDate={data?.latestFeedDate}
          rowTitle={
            isMobile
              ? t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_FORECASTED_MOBILE')
              : t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_FORECASTED')
          }
          unit="MWh"
          monthData={data?.monthlyOverview.map((o) => convertMeasure(o.totalForecasted, 'MWh').value)}
          yearSummary={data && convertMeasure(data.totalForecastedSum, 'MWh').value}
          contextInfo={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_FORECASTED_TOOLTIP')}
          color={theme.palette.charts.colors[1]}
        />
      )}

      {!isFluctuating(marketLocation.technologies) && !isControlEnergy(marketLocation.technologies) && (
        <AnnualOverviewRow
          latestFeedDate={data?.latestFeedDate}
          rowTitle={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_FORECASTED_MEAN')}
          unit="kW"
          monthData={data?.monthlyOverview.map((o) => convertMeasure(o.meanForecasted, 'kW').value)}
          yearSummary={data && convertMeasure(data.totalForecastedMean, 'kW').value}
          contextInfo={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_FORECASTED_MEAN_TOOLTIP')}
        />
      )}

      <AnnualOverviewRow
        latestFeedDate={data?.latestFeedDate}
        rowTitle={
          isMobile
            ? t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_PRODUCTION_MOBILE')
            : t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_PRODUCTION')
        }
        unit="MWh"
        monthData={data?.monthlyOverview.map((o) => convertMeasure(o.totalMeasurement, 'MWh').value)}
        yearSummary={data && convertMeasure(data.totalMeasurementSum, 'MWh').value}
        contextInfo={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_PRODUCTION_TOOLTIP')}
        color={theme.palette.charts.colors[6]}
      />

      {marketLocation.linkedMarketLocation && (
        <AnnualOverviewRow
          latestFeedDate={data?.latestFeedDate}
          rowTitle={
            isMobile
              ? t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_CONSUMPTION_MOBILE')
              : t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_CONSUMPTION')
          }
          unit="MWh"
          monthData={data?.monthlyOverview.map((o) => convertMeasure(o.totalConsumptionMeasurement, 'MWh').value)}
          yearSummary={data && convertMeasure(data.totalConsumptionMeasurementSum, 'MWh').value}
          color={theme.palette.charts.colors[6]}
        />
      )}

      <AnnualOverviewRow
        latestFeedDate={data?.latestFeedDate}
        rowTitle={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_PRODUCTION_MEAN')}
        unit="kW"
        monthData={data?.monthlyOverview.map((o) => convertMeasure(o.meanMeasurement, 'kW').value)}
        yearSummary={data && convertMeasure(data.totalMeasurementMean, 'kW').value}
        contextInfo={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_PRODUCTION_MEAN_TOOLTIP')}
      />

      {marketLocation.linkedMarketLocation && (
        <AnnualOverviewRow
          latestFeedDate={data?.latestFeedDate}
          rowTitle={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_CONSUMPTION_MEAN')}
          unit="kW"
          monthData={data?.monthlyOverview.map((o) => convertMeasure(o.meanConsumptionMeasurement, 'kW').value)}
          yearSummary={data && convertMeasure(data.totalConsumptionMeasurementMean, 'kW').value}
        />
      )}

      {isBiogas(marketLocation.technologies) && (
        <AnnualOverviewRow
          latestFeedDate={data?.latestFeedDate}
          rowTitle={
            <Trans components={{ sub: <sub /> }}>
              MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_MEASUREMENT_ABOVE_85_PERCENT_INSTALLED_POWER
            </Trans>
          }
          monthData={data?.monthlyOverview.map((o) =>
            new Date(year, getMonthNumber(o.month)) <= startOfToday()
              ? o.totalMeasuredFeedsAtLeast85PercentOfCapacity
              : null,
          )}
          yearSummary={data?.totalMeasuredFeedsAtLeast85PercentOfCapacity}
          contextInfo={t(
            'MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_MEASUREMENT_ABOVE_85_PERCENT_INSTALLED_POWER_TOOLTIP',
          )}
        />
      )}

      {marketLocation.permissions.seeRevenue &&
        !isBattery(marketLocation.technologies) &&
        !isFluctuating(marketLocation.technologies) && (
          <AnnualOverviewRow
            disableMonthTooltip={true}
            rowTitle={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_SPECIFIC_ADDITIONAL_REVENUE')}
            unit="ct/kWh"
            monthData={data?.monthlyOverview.map((o) => convertMeasure(o.specificAdditionalRevenue, 'ct/kWh').value)}
            yearSummary={'–'}
            fractionDigits={2}
            yearSummaryTooltip={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_NOT_AVAILABLE_METRIC')}
            contextInfo={t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_SPECIFIC_ADDITIONAL_REVENUE_TOOLTIP')}
          />
        )}
    </div>
  );
}
